import { LitElement, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';

import { emit } from '@amityeko/pbc-core/dist/utilities/event';
import { withAuth } from '@amityeko/pbc-core/dist/providers/auth/consumer-mixin';

import '@amityeko/design-system/dist/elements/typography/tg-title';
import '@amityeko/design-system/dist/elements/typography/tg-body-two';
import '@amityeko/design-system/dist/elements/dialog';

import type AmityekoDialog from '@amityeko/design-system/dist/elements/dialog';
import '../recognition-preview';

import { Recognition } from './types';
import { RECOGNITION_TYPE } from '../create-recognition/constants';
import styles from './styles';

@customElement('rnr-create-recognition-preview')
export default class RnRCreateRecognitionPreview extends withAuth(LitElement) {
  static styles = styles;

  @query('#dialog') dialog!: AmityekoDialog;
  @query('#quit_dialog') quitDialog!: AmityekoDialog;

  @property()
  receiverId?: string;

  @property()
  receiverDisplayName?: string;

  @property({ type: Number })
  previewScale?: number;

  @state()
  recognition?: Recognition;

  @state()
  animation?: string;

  @state()
  isLoading: boolean = false;

  public async show(recognition: any = {}) {
    this.dialog.show();
    if (Object.keys(recognition).length > 0) {
      this.recognition = recognition;

      //call the badge detail api to get animation id
      if (recognition?.badge) {
        try {
          const badgeDetail = await this.auth?.http?.get(
            `/api/v1/badges/${recognition?.badge?.id}`,
          );
          // call animation api
          const animationSrc = await this.auth?.http?.media(
            `/api/v1/media/images/${badgeDetail?.animation}`,
          );
          this.animation = animationSrc;
        } catch (e: any) {
          emit(this, 'amityeko-notification--toast', {
            detail: { message: e?.error.message, variant: 'error' },
          });
        }
      }
    }
  }

  private _onTriggerClick() {
    this.dialog.show();
  }

  private async _onConfirmPreview(e: CustomEvent) {
    e.stopPropagation();
    try {
      this.isLoading = true;
      emit(this, 'amityeko-notification--toast', {
        detail: { message: 'Please wait.. we’re processing your submission.', variant: 'info' },
      });

      let videoKey;
      if (this.recognition?.videoData) {
        const formData = new FormData();
        const fileName = 'recordVideo.mp4';
        formData.append('file', this.recognition?.videoData, fileName);
        const response = await this.auth?.http?.upload('/api/v1/media/videos', formData);
        videoKey = response[0].key;
      }
      let payload;
      const type = this.recognition?.type;
      switch (type) {
        case RECOGNITION_TYPE.TEXT:
          if (this.recognition?.background.type === 'image') {
            payload = {
              badge: {
                id: this.recognition?.badge?.id,
                updatedAt: this.recognition?.badge?.updatedAt,
              },
              backgroundColors: [
                this.recognition?.background?.fromColor || '#3DDBD9',
                this.recognition?.background?.toColor || '#0072C3',
              ],
              imageLayout: this.recognition?.images?.layout,
              medias: this.recognition?.images?.imageKeys,
              backgroundImage: {
                type: 'image',
                key: this.recognition?.background?.id,
              },
              text: this.recognition?.message,
              textColor: this.recognition?.textColor,
              type: this.recognition?.type,
              receiverId: this.receiverId,
              mediaGiphyId: this.recognition?.gif?.id,
            };
          } else {
            payload = {
              badge: {
                id: this.recognition?.badge?.id,
                updatedAt: this.recognition?.badge?.updatedAt,
              },
              backgroundColors: [
                this.recognition?.background?.fromColor || '#3DDBD9',
                this.recognition?.background?.toColor || '#0072C3',
              ],
              imageLayout: this.recognition?.images?.layout,
              medias: this.recognition?.images?.imageKeys,
              text: this.recognition?.message,
              textColor: this.recognition?.textColor,
              type: this.recognition?.type,
              receiverId: this.receiverId,
              mediaGiphyId: this.recognition?.gif?.id,
            };
          }
          break;
        case RECOGNITION_TYPE.VIDEO:
          payload = {
            badge: {
              id: this.recognition?.badge?.id,
              updatedAt: this.recognition?.badge?.updatedAt,
            },
            backgroundColors: [
              this.recognition?.background?.fromColor || '#3DDBD9',
              this.recognition?.background?.toColor || '#0072C3',
            ],
            medias: [{ type: 'video', key: videoKey }],
            text: this.recognition?.videoTitle,
            textColor: this.recognition?.videoTitleColor,
            type: this.recognition?.type,
            receiverId: this.receiverId,
            meta: {
              video: {
                isTitleEmbedded: false,
              },
            },
          };
          break;
        case RECOGNITION_TYPE.AUDIO:
          //TODO: to update payload for audio
          if (this.recognition?.background.type === 'image') {
            payload = {
              badge: {
                id: this.recognition?.badge?.id,
                updatedAt: this.recognition?.badge?.updatedAt,
              },
              backgroundColors: [
                this.recognition?.background?.fromColor || '#3DDBD9',
                this.recognition?.background?.toColor || '#0072C3',
              ],
              imageLayout: this.recognition?.images?.layout,
              medias: this.recognition?.images?.imageKeys,
              backgroundImage: {
                type: 'image',
                key: this.recognition?.background?.id,
              },
              text: this.recognition?.message,
              textColor: this.recognition?.textColor,
              type: this.recognition?.type,
              receiverId: this.receiverId,
              mediaGiphyId: this.recognition?.gif?.id,
            };
          } else {
            payload = {
              badge: {
                id: this.recognition?.badge?.id,
                updatedAt: this.recognition?.badge?.updatedAt,
              },
              backgroundColors: [
                this.recognition?.background?.fromColor || '#3DDBD9',
                this.recognition?.background?.toColor || '#0072C3',
              ],
              imageLayout: this.recognition?.images?.layout,
              medias: this.recognition?.images?.imageKeys,
              text: this.recognition?.message,
              textColor: this.recognition?.textColor,
              type: this.recognition?.type,
              receiverId: this.receiverId,
              mediaGiphyId: this.recognition?.gif?.id,
            };
          }
          break;
        default:
          payload = null;
          break;
      }

      const createdRecognition = await this.auth?.http?.post('/api/v2/recognitions', payload);
      emit(this, 'amityeko-notification--toast', {
        detail: { message: 'Recognition Sent', variant: 'success' },
      });
      emit(this, 'rnr-create-recognition-preview-recognition-created', {
        detail: { recognitionId: createdRecognition.id },
      });
      this.isLoading = false;
      this.dialog.hide();
    } catch (e: any) {
      this.isLoading = false;
      emit(this, 'amityeko-notification--toast', {
        detail: { message: e?.error?.message, variant: 'error' },
      });
    }
  }

  private _onDialogBack() {
    this.dialog.hide();
    emit(this, 'rnr-create-recognition-preview-dialog-back');
  }

  private _onDialogCancel() {
    this.quitDialog.show();
  }

  private _onConfirmQuit() {
    emit(this, 'rnr-edit-quit-dialog');
    this.quitDialog.hide();
  }

  private _onCancelQuit() {
    this.quitDialog.hide();
    this.show();
  }

  protected render() {
    const confirmButtonText = this.receiverDisplayName
      ? `Send to ${this.receiverDisplayName}`
      : 'Send';

    return html`
      <slot @click=${this._onTriggerClick}></slot>

      <amityeko-dialog
        id="dialog"
        .label=${'Preview'}
        .confirmButtonText=${confirmButtonText}
        ?hasSuffixIconButton=${true}
        .confirmButtonType=${'chip'}
        ?hasBackButton=${true}
        ?isLoading=${this.isLoading}
        ?shouldShowFooter=${true}
        @amityeko-back=${this._onDialogBack}
        @amityeko-confirm=${this._onConfirmPreview}
        @amityeko-cancel=${this._onDialogCancel}
      >
        <div class="content">
          <rnr-recognition-preview
            .scale=${this.previewScale}
            .badgeSrc=${this.recognition?.badge?.imageSrc || ''}
            .badgeName=${this.recognition?.badge?.cardTitle}
            .badgePoint=${this.recognition?.badge?.points}
            .bgFromColor=${this.recognition?.background?.fromColor}
            .bgToColor=${this.recognition?.background?.toColor}
            .bgImage=${this.recognition?.background?.src}
            .animation=${this.animation}
            .textColor=${this.recognition?.textColor}
            .text=${this.recognition?.message}
            .gif=${this.recognition?.gif}
            .gifId=${this.recognition?.gif?.id}
            .images=${this.recognition?.images}
            .videoURL=${this.recognition?.videoURL}
            .videoTitle=${this.recognition?.videoTitle}
            .videoTitleColor=${this.recognition?.videoTitleColor}
            ?isTitleEmbedded=${false}
            .audioURL=${this.recognition?.audioURL}
            .audioTitle=${this.recognition?.audioTitle}
            .audioTitleColor=${this.recognition?.audioTitleColor}
          ></rnr-recognition-preview>
        </div>
      </amityeko-dialog>

      <!--Quit Editing Dialog -->
      <amityeko-dialog
        id="quit_dialog"
        .variant=${'danger'}
        ?hasIconAsLabel=${true}
        .cancelButtonText=${'Continue editing'}
        .confirmButtonText=${'Yes, quit editing'}
        @amityeko-confirm=${this._onConfirmQuit}
        @amityeko-cancel=${this._onCancelQuit}
      >
        <div class="quit-dialog-content">
          <amityeko-tg-title>Quit editing?</amityeko-tg-title>
          <amityeko-tg-body-two>Changes you made so far will not be saved.</amityeko-tg-body-two>
        </div>
      </amityeko-dialog>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rnr-create-recognition-preview': RnRCreateRecognitionPreview;
  }
}
